import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "../axios";
import SightingModal from "../components/SightingModal";
import { useNavigate, useParams } from "react-router-dom";
import { getDayType, COL } from "../utils/dayType";
import { useDayTypeVersion } from "../components/DayTypeProvider";
import { useRegularMarks, wantsRegularMarks, RegularMark } from "../components/RegularMark";

const formatTime = (timeStr) => {
  if (!timeStr) return "";
  return timeStr.replace(/:/g, "").slice(0, 4);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  return `${year.slice(-2)}/${month}/${day}`;
};

function DepotPage({ currentUser }) {
  // 特別日が API から届いたら描き直す（日付の色分けが getDayType に依存している）
  useDayTypeVersion();

  const navigate = useNavigate();
  const { depot: depotParam } = useParams();
  const [depot, setDepot] = useState("");
  const [data, setData] = useState([]);
  const [allDepots, setAllDepots] = useState([]);
  const [routeColors, setRouteColors] = useState({});
  const [modalData, setModalData] = useState({
    isOpen: false,
    bus_number: "",
    date: "",
    sightings: [],
    depot: "",
  });
  const [loading, setLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedBus, setHighlightedBus] = useState(null);
  const tableContainerRef = useRef(null);
  const searchInputRef = useRef(null);

  const fetchRoutes = useCallback(async (targetDepot) => {
    try {
      const res = await axios.get(`/routes?depot=${encodeURIComponent(targetDepot)}`);
      const colorMap = {};
      res.data.forEach((r) => {
        colorMap[r.short_name] = r.color || "#9e9e9e";
      });
      setRouteColors(colorMap);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchDepots = useCallback(async () => {
    try {
      const res = await axios.get("/depots");
      setAllDepots(res.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchSightings = useCallback(async (targetDepot, { silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const res = await axios.get(
        `/sightings-matrix?depot=${encodeURIComponent(targetDepot)}`
      );
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!depotParam) return;
    const decodedDepot = decodeURIComponent(depotParam);
    setDepot(decodedDepot);
    fetchSightings(decodedDepot);
    fetchRoutes(decodedDepot);
    fetchDepots();
  }, [depotParam, fetchSightings, fetchRoutes, fetchDepots]);

  const openModal = (bus_number, date, sightings, depotName) => {
    setModalData({ isOpen: true, bus_number, date, sightings, depot: depotName });
  };
  const closeModal = () => setModalData((prev) => ({ ...prev, isOpen: false }));

  const handlePosted = async (message) => {
    closeModal();
    if (depot) await fetchSightings(depot, { silent: true });
    setToastMessage(message || "✅ 処理が完了しました！");
  };

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (modalData.isOpen) return;
      if (e.key === "Escape") {
        setSearchQuery("");
        setHighlightedBus(null);
        searchInputRef.current?.blur();
        return;
      }
      if (/^\d$/.test(e.key) && document.activeElement !== searchInputRef.current) {
        e.preventDefault();
        setSearchQuery((prev) => prev + e.key);
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [modalData.isOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    if (!tableContainerRef.current) return;
    const rows = tableContainerRef.current.querySelectorAll("tr[data-bus]");
    for (const row of rows) {
      if (row.dataset.bus.includes(q)) {
        setHighlightedBus(row.dataset.bus);
        row.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
    }
    setHighlightedBus(null);
  };

  const handleDepotChange = (e) => {
    const target = e.target.value;
    if (target) {
      navigate(`/depots/${encodeURIComponent(target)}`);
    }
  };

  const regularIds = useRegularMarks(currentUser, { depot }, data);
  // 表に印が 1 つも無いときは幅も空けない（何も無いのに時刻が右へ寄って見える）
  const marksOn = wantsRegularMarks(currentUser) && regularIds.size > 0;

  const getMaxDisplay = () => currentUser?.max_sightings_display || Infinity;

  // 最新8日間（今日含む）
  const dates = Array.from({ length: 8 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  });

  return (
    <div style={{ padding: "12px 8px", position: "relative" }}>

      {/* ── ローディング ── */}
      {loading && (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "20px 16px",
          color: "#065f46",
          fontSize: "14px",
        }}>
          <div style={{
            width: 18,
            height: 18,
            border: "2.5px solid #a7f3d0",
            borderTopColor: "#059669",
            borderRadius: "50%",
            animation: "spin 0.7s linear infinite",
            flexShrink: 0,
          }} />
          読み込み中...
        </div>
      )}

      {/* ── 空状態 ── */}
      {!loading && data.length === 0 && (
        <div style={{
          padding: "32px 16px",
          textAlign: "center",
          color: "#6b7280",
          fontSize: "14px",
        }}>
          <div style={{ fontSize: "28px", marginBottom: "8px" }}>📋</div>
          目撃情報が見つかりません
        </div>
      )}

      {/* ── テーブル ── */}
      {!loading && data.length > 0 && (
        <div ref={tableContainerRef} style={{ overflowX: "auto", overflowY: "auto", maxHeight: "calc(100vh - 136px)" }}>
          {Array.from(new Set(data.map((v) => v.depot))).map((depotName) => (
            <div key={depotName} style={{ marginBottom: "32px" }}>

              {/* 営業所名ヘッダー */}
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                margin: "0 0 12px",
                padding: "11px 16px 11px 18px",
                borderLeft: "4px solid #047857",
                borderRadius: "0 8px 8px 0",
                background: "linear-gradient(90deg, #ecfdf5 0%, #f8fffe 70%, #fff 100%)",
                boxShadow: "0 1px 4px rgba(4,120,87,0.08)",
              }}>
                <span style={{
                  fontSize: "1.1rem",
                  fontWeight: "700",
                  color: "#065f46",
                  letterSpacing: "0.05em",
                }}>
                  {depotName}
                </span>
                <span style={{
                  fontSize: "10px",
                  fontWeight: "700",
                  color: "#6ee7b7",
                  letterSpacing: "0.15em",
                }}>
                  営業所
                </span>
              </div>

              <table style={{
                borderCollapse: "collapse",
                width: "100%",
                textAlign: "center",
                tableLayout: "auto",
                fontSize: "13px",
              }}>
                <thead>
                  <tr>
                    <th style={{
                      padding: "8px 10px",
                      position: "sticky",
                      top: 0,
                      left: 0,
                      zIndex: 10,
                      background: "#047857",
                      color: "#fff",
                      fontWeight: "700",
                      fontSize: "12px",
                      letterSpacing: "0.04em",
                      borderRight: "2px solid #065f46",
                      borderBottom: "2px solid #065f46",
                    }}>
                      車号
                    </th>
                    {dates.map((date) => {
                      const cs = COL[getDayType(date)];
                      return (
                        <th key={date} style={{
                          padding: "8px 10px",
                          position: "sticky",
                          top: 0,
                          zIndex: 6,
                          background: cs.header,
                          color: "#fff",
                          fontWeight: "600",
                          fontSize: "12px",
                          whiteSpace: "nowrap",
                          borderRight: `1px solid ${cs.hBorder}`,
                          borderBottom: `2px solid ${cs.hBorder}`,
                        }}>
                          {formatDate(date)}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {data
                    .filter((v) => v.depot === depotName)
                    .map((bus, rowIdx) => (
                      <tr key={bus.bus_number}
                        data-bus={bus.bus_number}
                        style={{ background: rowIdx % 2 === 0 ? "#f0fdf4" : "#fff" }}
                      >
                        <td style={{
                          fontWeight: "700",
                          fontSize: "13px",
                          color: "#047857",
                          background: highlightedBus === bus.bus_number ? "#fef08a" : rowIdx % 2 === 0 ? "#dcfce7" : "#ecfdf5",
                          padding: "6px 8px",
                          verticalAlign: "middle",
                          position: "sticky",
                          left: 0,
                          zIndex: 4,
                          borderRight: "2px solid #a7f3d0",
                          borderBottom: "1px solid #d1fae5",
                          whiteSpace: "nowrap",
                        }}>
                          {bus.bus_number}
                        </td>
                        {dates.map((date) => {
                          const dayDataList = bus.sightings.filter((d) => d.date === date);
                          const records = dayDataList.flatMap((d) => d.records || []);
                          const grouped = {};
                          records.forEach((s) => {
                            const routeName = s.route || "";
                            if (!grouped[routeName]) grouped[routeName] = [];
                            grouped[routeName].push({ text: `${s.start}${formatTime(s.time)}`, regular: regularIds.has(s.postId) });
                          });
                          Object.keys(grouped).forEach((r) =>
                            grouped[r].sort((a, b) => parseInt(a.text.slice(-4)) - parseInt(b.text.slice(-4)))
                          );
                          const maxCount = getMaxDisplay();
                          const cs = COL[getDayType(date)];
                          const baseBg = rowIdx % 2 === 0 ? cs.cellEven : cs.cellOdd;

                          return (
                            <td key={date}
                              onClick={() => openModal(bus.bus_number, date, records, bus.depot)}
                              style={{
                                verticalAlign: "top",
                                cursor: "pointer",
                                padding: "5px 6px",
                                background: baseBg,
                                borderRight: "1px solid #d1fae5",
                                borderBottom: "1px solid #d1fae5",
                                transition: "background 0.12s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = cs.hover;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = baseBg;
                              }}
                            >
                              {records.length > 0 ? (
                                Object.entries(grouped).map(([route, starts]) => {
                                  const displayStarts = starts.slice(0, maxCount);
                                  const overflowCount = starts.length - displayStarts.length;
                                  return (
                                    <div key={route} style={{ marginBottom: "4px" }}>
                                      <span style={{
                                        backgroundColor: routeColors[route] || "#9e9e9e",
                                        color: "#fff",
                                        padding: "1px 6px",
                                        borderRadius: "6px",
                                        display: "inline-block",
                                        marginBottom: "2px",
                                        fontSize: "11px",
                                        fontWeight: "600",
                                      }}>
                                        {route}
                                      </span>
                                      {displayStarts.map((s, idx) => (
                                        <div key={idx} style={{ fontSize: "12px", color: "#374151", whiteSpace: "nowrap" }}>
                                          <RegularMark on={s.regular} align={marksOn} />{s.text}
                                        </div>
                                      ))}
                                      {overflowCount > 0 && (
                                        <div style={{ fontSize: "11px", color: "#059669", fontWeight: "600" }}>
                                          +{overflowCount}件
                                        </div>
                                      )}
                                    </div>
                                  );
                                })
                              ) : (
                                <span style={{ color: "#d1d5db", fontSize: "12px" }}>—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      <SightingModal
        isOpen={modalData.isOpen}
        onClose={closeModal}
        onPosted={handlePosted}
        depot={modalData.depot}
        bus_number={modalData.bus_number}
        date={modalData.date}
        sightings={modalData.sightings}
        currentUser={currentUser}
      />

      {toastMessage && (
        <div style={{
          position: "fixed",
          bottom: "30px",
          left: "50%",
          transform: "translateX(-50%)",
          backgroundColor: "#1f2937",
          color: "#fff",
          padding: "10px 20px",
          borderRadius: "8px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.25)",
          zIndex: 2000,
          fontSize: "14px",
          animation: "fadeInOut 3s ease",
        }}>
          {toastMessage}
        </div>
      )}

      {/* ── フッター（検索・営業所切替） ── */}
      <div style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        background: "linear-gradient(180deg, #ecfdf5 0%, #d1fae5 100%)",
        borderTop: "2px solid #6ee7b7",
        boxShadow: "0 -2px 10px rgba(4,120,87,0.12)",
      }}>
        <div style={{
          maxWidth: "640px",
          margin: "0 auto",
          padding: "8px 12px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}>
        {/* 車号検索 */}
        <form onSubmit={handleSearch} style={{ display: "flex", alignItems: "center", gap: "6px", flex: 1 }}>
          <span
            onClick={() => { setSearchQuery(""); setHighlightedBus(null); }}
            style={{ fontSize: "11px", fontWeight: "700", color: "#047857", letterSpacing: "0.04em", textAlign: "center", lineHeight: "1.3", userSelect: "none" }}
          >
            車号<br />検索
          </span>
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); if (!e.target.value.trim()) setHighlightedBus(null); }}
            placeholder="1100"
            inputMode="numeric"
            style={{
              flex: "0 0 58px",
              minWidth: 0,
              maxWidth: "58px",
              border: "1.5px solid #6ee7b7",
              borderRadius: "8px",
              padding: "5px 10px",
              fontSize: "16px",
              outline: "none",
              background: "#fff",
              color: "#065f46",
            }}
          />
          <button type="submit" style={{
            background: "#047857",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            padding: "5px 12px",
            fontSize: "12px",
            fontWeight: "700",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}>
            移動
          </button>
        </form>

        {/* 営業所切替 */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span style={{ fontSize: "11px", fontWeight: "700", color: "#047857", whiteSpace: "nowrap", letterSpacing: "0.04em" }}>
            営業所
          </span>
          <select
            value={depot}
            onChange={handleDepotChange}
            style={{
              border: "1.5px solid #6ee7b7",
              borderRadius: "8px",
              padding: "5px 8px",
              fontSize: "12px",
              background: "#fff",
              color: "#065f46",
              fontWeight: "600",
              cursor: "pointer",
              maxWidth: "140px",
            }}
          >
            {allDepots.map((d) => (
              <option key={d.id} value={d.short_name}>{d.short_name}</option>
            ))}
          </select>
        </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateY(10px) translateX(-50%); }
          10%, 90% { opacity: 1; transform: translateY(0) translateX(-50%); }
          100% { opacity: 0; transform: translateY(10px) translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

export default DepotPage;
